import * as Sentry from "@sentry/react";
import { ShieldAlert } from "lucide-react";
import { useCallback, useEffect, useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";

import { getDraftById } from "../../../api/drafts-v2";
import type { Draft, QualityItem } from "../../../api/types/draft";
import { useOnInvalidate } from "../context/CaseIdeContext";

type Props = { draftId: string };

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

/**
 * What the banner lists. With a notice scorecard on the draft, its strip
 * already names each unanswered issue, so only the `should` lines remain.
 */
export const visibleQuality = (
  draft: Draft | null
): { headline: string; items: QualityItem[] } | null => {
  const report = draft?.quality_report;
  if (!report?.headline) return null;
  const scored = Boolean(draft?.notice_scorecard?.issues.length);
  const items = scored
    ? report.items.filter((i) => i.severity === "should")
    : report.items;
  if (!items.length) return null;
  const headline =
    scored && report.headline_kind === "unanswered"
      ? plural(items.length, "finding to review", "findings to review")
      : report.headline;
  return { headline, items };
};

/**
 * Above a delivered brief or notice response: what the drafting run's
 * evidence-plan and review gates left short ("2 issues answered without
 * evidence"), expandable to each line.
 */
export const DraftQualityStrip: FC<Props> = ({ draftId }) => {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    getDraftById(draftId)
      .then(setDraft)
      .catch((error) => Sentry.captureException(error));
  }, [draftId]);

  useEffect(load, [load]);
  useOnInvalidate(["drafts"], load);

  const shown = visibleQuality(draft);
  if (!shown) return null;

  return (
    <div
      data-testid="draft-quality-strip"
      className="border-b border-line-1 bg-fill-1 px-3 py-1.5 text-sm text-fg-2"
    >
      <div className="flex items-center gap-2">
        <ShieldAlert className="size-3.5 shrink-0 text-warn" aria-hidden />
        <span className="text-fg">{shown.headline}</span>
        <Button
          variant="link"
          size="xs"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "Hide" : `Show ${shown.items.length}`}
        </Button>
      </div>
      {open ? (
        <ul className="mt-1 divide-y divide-line-1">
          {shown.items.map((item, index) => (
            <li
              key={`${index}-${item.text}`}
              className="flex items-start gap-2 py-1"
              data-testid="draft-quality-item"
            >
              <StatusBadge tone={item.severity === "must" ? "danger" : "warn"}>
                {item.severity === "must" ? "Must Fix" : "Review"}
              </StatusBadge>
              <span className="min-w-0 flex-1">{item.text}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};
